import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Avatar,
  TextField,
  InputAdornment,
  Collapse,
  Grid,
  useTheme,
  TablePagination,
} from '@mui/material';

import {
    Person,
  Search,
  Groups
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Crew, Meta } from '../../types';
import { crewApi } from '../../services/api';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { extractApiError, unwrapListResponse, unwrapMetaResponse } from '../../apiUnwrapper/apiUnwrapper';
import { useSnackbar } from 'notistack';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  PageHeader,
} from '../../components/SharedPageParts';
import { useAuth } from '../../services/AuthContext';

const CrewPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { user } = useAuth();
    const canManageCrew = user?.roleName === 'Admin';
    const [open, setOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [openConfirm, setOpenConfirm] = useState(false);
    const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});
    const [confirmMessage, setConfirmMessage] = useState("Are you sure?");
    const [crewMeta, setCrewMeta] = useState<Meta>({ total: 0, size: 5, page: 1 });
    
      const handleSubmit = async () => {
        
        try{
          const createdCrew = await crewApi.createUpdateCrew({name: crewName});
          loadCrew(crewMeta.page, crewMeta.size);
          setOpen(false);
          setCrewName('');
          setShowNewCrewForm(false);
          enqueueSnackbar('Crew created successfully!', { variant: 'success' });
        }
        catch(error){
          console.error('Error creating crew:', error);
          const err  = extractApiError(error);
          enqueueSnackbar('Failed to create crew - ' + err.message, { variant: 'error' });
        }
      };

      const [error, setError] = useState<string | null>(null);
      const [searchTerm, setSearchTerm] = useState('');
      const [showNewCrewForm, setShowNewCrewForm] = useState(false);
      const [crewName, setCrewName] = useState('');
      const [loading, setLoading] = useState(true);
      const [crew, setCrew] = useState<Crew[]>([]);
      const [editCrewId, setEditCrewId] = useState<number | null>(null);
      const [editCrewName, setEditCrewName] = useState('');

      const startEdit = (cr: Crew) => {
          setEditCrewId(cr.id);
          setEditCrewName(cr.name);
        };
    
      useEffect(() => {
         loadCrew(crewMeta.page, crewMeta.size);
        }, []);

        const loadCrew = async (page = 1, size = 5) => {
            try {
              setLoading(true);
              setError(null);
              const crewData = await crewApi.getCrew(page, size);
              setCrew(unwrapListResponse<Crew>(crewData));
              setCrewMeta(unwrapMetaResponse<Meta>(crewData) || { total: 0, size: 5, page: 1 });
            } catch (err) {
              setError('Failed to load Crew. Make sure the backend is running on port 8081.');
              console.error('Error loading Crew:', err);
            } finally {
              setLoading(false);
            }
          };

          const searchCrew = async () => {
            try {
              setLoading(true);
              setError(null);
              const crewData = await crewApi.getCrewForSearchTerm(searchTerm, crewMeta.page, crewMeta.size);
              setCrew(unwrapListResponse<Crew>(crewData));
              setCrewMeta(unwrapMetaResponse<Meta>(crewData) || { total: 0, size: 5, page: 1 });
            } catch (err) {
              setError('Failed to load Crew.');
              console.error('Error loading Crew:', err);
            } finally {
              setLoading(false);
            }
          };

          const handleUpdate = async () => {
            try {
              if (editCrewId === null) return;
              await crewApi.createUpdateCrew({
                id: editCrewId,
                name: editCrewName,
              });
              loadCrew(crewMeta.page, crewMeta.size);
              setEditCrewId(null);
              setEditCrewName('');
              enqueueSnackbar('Crew updated successfully!', { variant: 'success' });
          } catch (error) {
            const err  = extractApiError(error);
            enqueueSnackbar('Failed to update crew - ' + err.message, { variant: 'error' });
          }};
          
      const handleDelete = async (id: number) => {
      try{
          setConfirmAction(() => async () => {
          await crewApi.deleteCrew(id);
          enqueueSnackbar("Crew deleted successfully", { variant: "success" });
          loadCrew(crewMeta.page, crewMeta.size);
          });
          setConfirmMessage("Are you sure you want to delete this Crew?");
          setOpenConfirm(true);
          }
          catch(error){
          enqueueSnackbar("Error deleting Crew: "+error , { variant: "error" });
          setError('Error deleting Crew: '+error );
          }
      };
      

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      {/* Main Content for Equipments Page can be added here */}
      <Box className="MuiBox-root-margin" sx={{ px: 1, mx: 'auto'}}>
        <PageHeader
                        title="Crew Management"
                        Icon={Groups}
                        onBack={() => navigate('/commondata')}
                        backLabel="Back to Common Data"
                        subtitle="Manage crew assignments and team organization."
                      />

        {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, border: '1px solid #ef4444' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
                                        placeholder="Search Crew by name..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            searchCrew(); // your API call
                                          }
                                        }}
                                        InputProps={{
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              <IconButton onClick={searchCrew} edge="end">
                                                <Search sx={{ color: theme.palette.text.secondary }} />
                                              </IconButton>
                                            </InputAdornment>
                                          ),
                                          sx: {
                                            backgroundColor: theme.palette.background.default,
                                            color: theme.palette.text.primary
                                          }
                                        }}
                                        sx={{
                                          flexGrow: 1,
                                          minWidth: 250,
                                          '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: theme.palette.divider },
                                            '&:hover fieldset': { borderColor: theme.palette.text.secondary },
                                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                          },
                                          '& .MuiInputBase-input': { color: theme.palette.text.primary }
                                        }}
                                      />
            {canManageCrew && (
              <Button
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
                }}
                onClick={() => setShowNewCrewForm(prev => !prev)}
              >
                {showNewCrewForm ? 'Hide Crew Form' : 'Add Crew'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Collapsible New Crew Form */}
      {canManageCrew && (
      <Collapse in={showNewCrewForm} timeout="auto" unmountOnExit>
        <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
              Add New Crew
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Crew Name"
                  variant="outlined"
                  value={crewName}
                  onChange={(e) => setCrewName(e.target.value.toUpperCase())}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: theme.palette.divider },
                      '&:hover fieldset': { borderColor: '#64748b' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
                mt: 1
              }}
                  onClick={() => {
                    handleSubmit(); 
                  }}
                >
                  Create
                </Button>
                <Button
                  variant="contained"
                  sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
                mt: 1
              }}
                  onClick={() => {
                    setCrewName('');
                    setShowNewCrewForm(false);
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
           
          </CardContent>
        </Card>
       </Collapse>
      )}

      <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
            Crew ({crewMeta.total})
          </Typography>
          <TablePagination
            component="div"
            count={crewMeta.total}
            page={crewMeta.page - 1}
            onPageChange={(e, newPage) => loadCrew(newPage + 1, crewMeta.size)}
            rowsPerPage={crewMeta.size}
            onRowsPerPageChange={(e) => loadCrew(1, parseInt(e.target.value, 10))}
            rowsPerPageOptions={[5, 10, 15, 20]}
            labelRowsPerPage="Crew per page"
          />
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.background.paper }}>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Avatar</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Name</TableCell>
                  {canManageCrew && (
                    <TableCell align="center" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {crew.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Person sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                          No Crew found
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {searchTerm ? 'Try adjusting your search terms' : 'Start the Spring Boot backend to load Crew'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  crew.map((cr) => (
                    <TableRow key={cr.id} hover sx={{ '&:hover': { backgroundColor: theme.palette.background.paper }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Avatar sx={{ bgcolor: '#667eea' }}>
                          {cr.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </TableCell>
                      
                      <TableCell sx={{ color: theme.palette.text.primary }}>
                        {editCrewId === cr.id ? (
                        <TextField
                         value={editCrewName}
                         sx={{backgroundColor: 'white', color:'black'}}
                         onChange={(e) => setEditCrewName(e.target.value.toUpperCase())}
                         >
                         </TextField>
                         ) : (
                         cr.name
                        )}
                         </TableCell>
                      {canManageCrew && (
                      <TableCell align="center" sx={{ borderBottom: 'none' }}>
                          {editCrewId === cr.id ? (
                            <>
                              <Button
                                variant="contained"
                                sx={{ backgroundColor: '#667eea', color: 'white', mr: 1 }}
                                onClick={handleUpdate}
                              >
                                Save
                              </Button>
                              <Button onClick={() => setEditCrewId(null)}>Cancel</Button>
                            </>
                          ) : (
                            <>
                              <IconButton onClick={() => startEdit(cr)}>
                                <EditIcon sx={{ color: '#667eea' }} />
                              </IconButton>
                              <IconButton onClick={() => handleDelete(cr.id)}>
                                <DeleteIcon sx={{ color: '#ef4444' }} />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                        )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={crewMeta.total}
            page={crewMeta.page - 1}
            onPageChange={(e, newPage) => loadCrew(newPage + 1, crewMeta.size)}
            rowsPerPage={crewMeta.size}
            onRowsPerPageChange={(e) => loadCrew(1, parseInt(e.target.value, 10))}
            rowsPerPageOptions={[5, 10, 15, 20]}
            labelRowsPerPage="Crew per page"
          />
          <ConfirmDialog
                    open={openConfirm}
                    onClose={() => setOpenConfirm(false)}
                    onConfirm={confirmAction}
                    title="Confirm Delete"
                    message={confirmMessage}
                  />
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
};
export default CrewPage;